import { computed, onMounted, onUnmounted, ref, Ref } from "vue";

export type CategorySwipeDirection = "left" | "right" | null;

export interface CategorySwipeOptions {
  /**
   * Callback при завершении свайпа (когда пользователь достиг порога и отпустил)
   */
  onSwipeComplete?: (direction: "prev" | "next") => void;

  /**
   * Порог в процентах от ширины экрана для срабатывания перехода
   * @default 30
   */
  threshold?: number;

  /**
   * Целевой элемент для отслеживания свайпов
   * Если не указан, будет использоваться document.body
   */
  targetElement?: Ref<HTMLElement | null | undefined>;

  /**
   * Отключить свайп функционал
   */
  disabled?: Ref<boolean>;

  /**
   * Кастомная функция проверки достижения порога
   * Если не указана, используется стандартная проверка по threshold
   */
  isThresholdReached?: (progress: number, progressNormalized: number, direction: CategorySwipeDirection) => boolean;
}

export interface CategorySwipeState {
  /**
   * Направление свайпа
   */
  direction: Ref<CategorySwipeDirection>;

  /**
   * Прогресс свайпа в процентах (0-100)
   */
  progress: Ref<number>;

  /**
   * Активен ли свайп в данный момент
   */
  isActive: Ref<boolean>;

  /**
   * Прогресс с учетом порога (0-100, где 100 = порог достигнут)
   */
  progressNormalized: Ref<number>;

  /**
   * Достигнут ли порог для навигации
   */
  isReadyToNavigate: Ref<boolean>;
}

/**
 * Композабл для обработки горизонтальных свайпов между категориями
 *
 * @example
 * ```typescript
 * const { direction, progress, isActive, progressNormalized } = useCategorySwipe({
 *   onSwipeComplete: (direction) => {
 *     if (direction === 'next') goToNextCategory();
 *     if (direction === 'prev') goToPrevCategory();
 *   },
 *   threshold: 30
 * });
 * ```
 *
 * @remarks
 * Композабл автоматически игнорирует touch события, начатые на элементах
 * внутри контейнеров с классом `.swipe-hor`. Это позволяет использовать
 * горизонтальный скролл внутри секций без конфликтов с навигацией между секциями.
 *
 * @example
 * ```html
 * <!-- Этот блок будет скроллиться горизонтально без перехвата событий -->
 * <div class="swipe-hor">
 *   <div class="horizontal-scroll-content">...</div>
 * </div>
 * ```
 */
export function useCategorySwipe(
  options: CategorySwipeOptions = {}
): CategorySwipeState {
  const { onSwipeComplete, threshold = 30, targetElement, disabled, isThresholdReached } = options;

  // Состояние свайпа
  const direction = ref<CategorySwipeDirection>(null);
  const progress = ref<number>(0);
  const isActive = ref<boolean>(false);

  // Touch state
  let startX = 0;
  let startY = 0;
  let currentX = 0;
  let isScrolling: boolean | null = null;
  let screenWidth = 0;

  // Вычисляемый прогресс с учетом порога
  const progressNormalized = computed(() => {
    const raw = progress.value;
    const normalized = (raw / threshold) * 100;
    return Math.min(100, normalized);
  });

  // Достигнут ли порог для навигации
  const isReadyToNavigate = computed(() => {
    if (!isActive.value) return false;
    
    // Если передана кастомная функция проверки, используем её
    if (isThresholdReached) {
      return isThresholdReached(progress.value, progressNormalized.value, direction.value);
    }
    
    // Иначе используем стандартную проверку
    return progress.value >= threshold;
  });

  const handleTouchStart = (e: TouchEvent) => {
    if (disabled?.value) return;

    const touch = e.touches[0];
    if (!touch) return;

    // Проверяем, не начато ли событие внутри элемента с горизонтальным свайпом
    const target = e.target as HTMLElement;
    if (target && target.closest('.swipe-hor')) {
      // Игнорируем события из элементов с горизонтальным скроллом
      return;
    }

    startX = touch.clientX;
    startY = touch.clientY;
    currentX = startX;
    isScrolling = null;
    screenWidth = window.innerWidth;
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (disabled?.value) return;
    if (!startX) return;

    const touch = e.touches[0];
    if (!touch) return;

    currentX = touch.clientX;
    const deltaX = currentX - startX;
    const deltaY = touch.clientY - startY;

    // Определяем направление скролла при первом движении
    if (isScrolling === null) {
      isScrolling = Math.abs(deltaY) > Math.abs(deltaX);
    }

    // Если вертикальный скролл, не обрабатываем
    if (isScrolling) {
      return;
    }

    // Предотвращаем стандартное поведение только для горизонтального свайпа
    e.preventDefault();

    const absDeltaX = Math.abs(deltaX);
    const progressPercent = (absDeltaX / screenWidth) * 100;

    // Обновляем состояние
    isActive.value = true;
    progress.value = progressPercent;
    direction.value = deltaX > 0 ? "right" : "left";
  };

  const handleTouchEnd = () => {
    if (disabled?.value) return;
    if (!isActive.value) return;

    // Проверяем, достигнут ли порог (используем единый флаг)
    const thresholdReached = isReadyToNavigate.value;

    if (thresholdReached && direction.value) {
      // Вызываем callback при достижении порога
      const swipeDirection = direction.value === "right" ? "prev" : "next";
      onSwipeComplete?.(swipeDirection);
    }

    // Сбрасываем состояние
    resetState();
  };

  const handleTouchCancel = () => {
    resetState();
  };

  const resetState = () => {
    isActive.value = false;
    progress.value = 0;
    direction.value = null;
    startX = 0;
    currentX = 0;
    isScrolling = null;
  };

  // Монтирование обработчиков
  onMounted(() => {
    const element = targetElement?.value || document.body;

    element.addEventListener("touchstart", handleTouchStart as any, {
      passive: true,
    });
    element.addEventListener("touchmove", handleTouchMove as any, {
      passive: false,
    });
    element.addEventListener("touchend", handleTouchEnd as any);
    element.addEventListener("touchcancel", handleTouchCancel as any);
  });

  // Размонтирование обработчиков
  onUnmounted(() => {
    const element = targetElement?.value || document.body;

    element.removeEventListener("touchstart", handleTouchStart as any);
    element.removeEventListener("touchmove", handleTouchMove as any);
    element.removeEventListener("touchend", handleTouchEnd as any);
    element.removeEventListener("touchcancel", handleTouchCancel as any);
  });

  return {
    direction,
    progress,
    isActive,
    progressNormalized,
    isReadyToNavigate,
  };
}
